import { orderBy } from "lodash"
// Business Role
export const adminRoleId = "ccedda33-ebe0-4e50-8215-49ca806c10a0"
export const tagRoleConsultant99 = "consultant_99_advisory"
export const tagRoleClient99 = "client_99_advisory"
// Business tag
export const tag99Advisory = "99advisory"
export const tagLyra = "lyra"
export const tagCSF = "csf"
export const tagAgircArrco = "agirc_arrco"
export const tagLazard = "lazard"
// Var
export const MANAGER = "MANAGER"
export const CONSULTANT_99 = "CONSULTANT_99"
export const CLIENT_99 = "CLIENT_99"

// Functions
export const hasOneOfTheseMandatoryRights = (rightsNames, rightsParams) => {
  const partialUserRights = getUserRights(rightsNames, rightsParams)
  return Object.values(partialUserRights).some(bool => bool)
}

export const hasAllOfTheseMandatoryRights = (rightsNames, rightsParams) => {
  const partialUserRights = getUserRights(rightsNames, rightsParams)
  return Object.values(partialUserRights).every(bool => bool)
}

export const getUserRights = (rightsNames, rightsParams) => {
  if (!Array.isArray(rightsNames)) return {}

  return rightsNames.reduce((acc, rightName) => {
    return { ...acc, [rightName]: userHasRightTo(rightName, rightsParams) }
  }, {})
}

export const userHasRightTo = (rightName, rightsParams) => {
  const { business, user, rolesOfUser } = rightsParams || {}
  const { useSeparatedRoles } = business || {}

  if (useSeparatedRoles) {
    return hasAccessWithSeparatedRoles(rightName, { business, user, rolesOfUser })
  }
  return checkAccess(business, user, rightName)
}

export const hasAccessWithSeparatedRoles = (rightName, { business, user, rolesOfUser }) => {
  if (!business || !user) return false
  const hasRoles = Array.isArray(rolesOfUser) && rolesOfUser.length > 0
  if (!hasRoles) return false
  const result = rolesOfUser.some(role => role?.rights?.[rightName] === true)
  return result
}

export const checkAccess = (myBusiness, user, accessRequested) => {
  if (!myBusiness || !user) return false
  let userRoleId = user.BUSINESS_ROLE
  let businessRoles = myBusiness?.businessAccess
  if (!Array.isArray(businessRoles)) return false

  const userRole = businessRoles.filter(role => role.accessId === userRoleId)
  const hasAccess = userRole && userRole[0] && userRole[0][accessRequested] === true
  return hasAccess
}

export const useGroup = myBusiness => myBusiness?.useGroup

export const isMSFBusiness = myBusiness => myBusiness?.useMSF

// Les business restés sur les vieux modules (gouvernance v1, MSF) n'ont pas d'équivalent
// de leur administration dans l'AdminConsole : ils gardent l'admin V1 (/business/informations).
// Les référentiels priment : dès qu'un business les active, il passe à l'admin moderne.
export const isLegacyAdminBusiness = myBusiness =>
  !myBusiness?.useReferentials &&
  Boolean(myBusiness?.isGovernanceBusiness || myBusiness?.useMSF)

export const isLyraBusiness = myBusiness => myBusiness?.tag === tagLyra

export const isCSFBusiness = myBusiness => myBusiness?.tag === tagCSF

export const is99AdviBusiness = myBusiness => myBusiness?.tag === tag99Advisory

export const isAgircArrcoBusiness = myBusiness => {
  const isTagAgir = myBusiness?.tag === tagAgircArrco
  // Ici c'est particulier car pour une démo on a besoin d'un compte de type 99advi mais sans vouloir que le compte est une trad spécifique
  // Donc temporairement on va aussi identifier le compte à l'aide du businessId
  const isAgircArccoProdAccountType99advi = myBusiness?._id?.$oid === "646b3ea7b5aa1c62c079355b"
  return isTagAgir || isAgircArccoProdAccountType99advi
}

export const isAdminRoleId = roleId => roleId === adminRoleId

export const isAdministrator = userInformation => {
  const roleId = userInformation?.BUSINESS_ROLE
  return isAdminRoleId(roleId)
}

export const onCheckManager = userInformation => {
  return isAdministrator(userInformation)
}

export const getUserType99 = (myBusiness, userInformation) => {
  let userType = ""
  const roleId = userInformation?.BUSINESS_ROLE
  const isBusinessTag99 = myBusiness?.tag === tag99Advisory
  const isAdmin = isAdministrator(userInformation)

  if (!Array.isArray(myBusiness?.businessAccess)) return userType
  const findRoleUser = myBusiness?.businessAccess.find(access => access?.accessId === roleId)
  const tagRoleUser = findRoleUser?.tag
  if (isAdmin && isBusinessTag99) userType = MANAGER
  else if (tagRoleUser === tagRoleConsultant99) userType = CONSULTANT_99
  else if (tagRoleUser === tagRoleClient99) userType = CLIENT_99
  return userType
}

export const getUserType99PerRoleId = (myBusiness, roleId) => {
  let userType = ""
  const isAdmin = isAdminRoleId(roleId)
  const isBusinessTag99 = myBusiness?.tag === tag99Advisory

  if (!Array.isArray(myBusiness?.businessAccess)) return userType
  const findRoleUser = myBusiness?.businessAccess.find(access => access?.accessId === roleId)
  const tagRoleUser = findRoleUser?.tag

  if (isAdmin && isBusinessTag99) userType = MANAGER
  else if (tagRoleUser === tagRoleConsultant99) userType = CONSULTANT_99
  else if (tagRoleUser === tagRoleClient99) userType = CLIENT_99
  return userType
}

export const canGetAllGroups = (myBusiness, userInformation) => {
  if (!myBusiness || !userInformation) return false
  const businessUseGroup = myBusiness?.useGroup
  const is99Advisory = myBusiness?.tag === tag99Advisory
  if (!is99Advisory) return businessUseGroup
  const userType = getUserType99(myBusiness, userInformation)
  const userCanUseGroups = [MANAGER, CONSULTANT_99].includes(userType)
  return businessUseGroup && userCanUseGroups
}

export const canGetMyGroups = myBusiness => myBusiness?.useGroup

export const isBusinessUseAuditPlans = myBusiness => myBusiness?.useAuditPlans

export const getMyBusinessUseControlsThematic = myBusiness => {
  if (!myBusiness) return false
  return Boolean(myBusiness?.paramsControls?.useControlsThematic)
}

export const getMyBusinessUseControlLevelLink = myBusiness => {
  if (!myBusiness) return false
  return myBusiness?.paramsControls?.useControlLevelLink
}

export const getMyBusinessUseRecoInControl = myBusiness => {
  if (!myBusiness) return false
  return myBusiness?.paramsControls?.useRecoInControl
}

export const getMyBusinessUseConformityScale = myBusiness => {
  if (!myBusiness?.paramsControls?.conformityScaleId) return false
  const conformityScaleId = myBusiness?.paramsControls?.conformityScaleId
  if (typeof conformityScaleId !== "string" || conformityScaleId === "") return false
  return true
}

export const getInfosGroupWorking = (userInformation, myGroup) => {
  // Met la liste des groups dans l'ordre alphabétique
  const orderMyListGroup = Array.isArray(myGroup) ? orderBy(myGroup, "name", "asc") : []
  const idGroupWorkDefault = userInformation?.WORKING_GROUP_ID
  // Cherche les infos du groupe de travail sélectionné
  const infoGroupWorkDefault = orderMyListGroup?.find(
    item => item?._id?.$oid === idGroupWorkDefault
  )
  // Si pas de groupe de travaille sélectionné ou prend le 1er groupes de la liste
  const groupWorkSelected =
    infoGroupWorkDefault === null || infoGroupWorkDefault === undefined
      ? orderMyListGroup[0]
      : infoGroupWorkDefault

  return groupWorkSelected
}

// Authorization workflow 99advisory
export const userTypeAuthorizedModifyRequestedDocs = [MANAGER, CONSULTANT_99, CLIENT_99]

export const controlParametersAuthorizedModifManager = [
  "_id",
  "targetUsers",
  "reference",
  "process",
  "color",
  "startCoveragePeriod",
  "endCoveragePeriod",
  "thematic",
  "department",
  "sample",
  "riskApproach",
  "referenceRegulatory",
  "objectifControl",
  "requestedDocuments",
  "filesAttachedIds",
  "riskImpact",
  "risk",
]

export const controlParametersAuthorizedModifConsultant = [
  "_id",
  "reference",
  "process",
  "color",
  "startCoveragePeriod",
  "endCoveragePeriod",
  "thematic",
  "department",
  "sample",
  "riskApproach",
  "referenceRegulatory",
  "objectifControl",
  "requestedDocuments",
  "riskImpact",
  "risk",
]

// Authorization props mapping Risk
export const controlPropsMappingRiskCanBePatched = [
  "_id",
  "color",
  "reference",
  "process",
  "sample",
  "sample",
  "comments_sample",
  "description",
  "comments",
  "objectifControl",
  "filesAttachedIds",
  "requestedDocuments",
  "targetUsers",
  "level",
  "type_control",
  "mitigantType",
  "riskIds",
  "executionTime",
  "thematicId",
  "parentControlsIds",
  "regulatoryReference",
]

export const responseStatusControlIsAnswered = [
  "validated",
  "validatedByManager",
  "validatedByClient",
]
